import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import Table from "@/components/Table/table";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useGetClaimTasksListByCaseQuery } from "@/services/tasks/tasksReducer";
import { useColumnTasksDefs } from "./taskTable/TasksColumnDefs";
import { useTasksCustomCellRenderers } from "./taskTable/TasksCellRenderer";
import TitleComplement from "./taskTable/TitleComplement";
import { getOnlyDate } from "@/utility/common";
import BasicSearch from "./taskTable/BasicSearch";
import "./Task.css";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { END_OF_DAY_TIME, START_OF_DAY_TIME } from "@/Constant";

const Tasks = () => {
  const [showBasicSearch, setShowBasicSearch] = useState(false);
  const retainState = useSelector((e: any) => e.retainState.tasksListTabState);
  const clearSearch = retainState.clearSearch;
  const { caseId } = useParams();
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const { t } = useTranslation();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [retainState]);
  const paginationObj = getPaginationObj;
  const [searchUrl, setSearchUrl] = useState<any>({
    caseId: caseId,
    ...retainState.formValues,
  });
  const [formValues, setFormValues] = useState<any>(
    retainState?.formValues || {
      ClaimReference: "",
      Name: "",
      Overdue: "",
      Status: "",
      Type: "",
      DueDateStart: "",
      DueDateEnd: "",
      CreatedOnStart: "",
      CreatedOnEnd: "",
    }
  );
  const columnTasksDefs = useColumnTasksDefs();
  const caseRenderers = useTasksCustomCellRenderers();

  const handleChange = (name: any, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  function handleSubmit() {
    setSearchUrl({
      ...formValues,
      caseId: caseId,
    });
    const params: any = {
      DueDateEnd: formValues.DueDateEnd
        ? `${getOnlyDate(formValues.DueDateEnd)} ${END_OF_DAY_TIME}`
        : "",
      DueDateStart: formValues.DueDateStart
        ? `${getOnlyDate(formValues.DueDateStart)} ${START_OF_DAY_TIME}`
        : "",
      CreatedOnEnd: formValues.CreatedOnEnd
        ? `${getOnlyDate(formValues.CreatedOnEnd)} ${END_OF_DAY_TIME}`
        : "",
      CreatedOnStart: formValues.CreatedOnStart
        ? `${getOnlyDate(formValues.CreatedOnStart)} ${START_OF_DAY_TIME}`
        : "",
    };
    setSearchUrl((prevUrl: any) => ({
      ...prevUrl,
      ...params,
    }));
    dispatch(
      setComponentState1({
        entityName: "tasksListTabState",
        entityState: {
          sortedColumnTypes: { ...resetSortStatus() },
          formValues: { ...formValues, ...params, caseId: caseId },
          clearSearch: true,
        },
      })
    );
  }
  let basicSearchPayloadJson = { ...searchUrl, ...paginationObj };
  const {
    data: tasksData,
    isFetching: isTasksFetching,
    isLoading: isTasksLoading,
    isError: tasksError,
    status: tasksStatus,
  } = useGetClaimTasksListByCaseQuery(
    { ...basicSearchPayloadJson },
    {
      skip: !searchUrl.caseId,
      refetchOnMountOrArgChange: true,
    }
  );
  const tableDataFetching = isTasksFetching;
  const tableDataError = tasksError;
  const tableDataHasRecords = tasksData?.hasMoreRecords;
  const tableData = tasksData?.results;
  const dispatch = useDispatch();
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "tasksListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const resetSortStatus = () => {
    let json: any = { ...retainState.sortedColumnTypes };
    for (const key in json) {
      json[key] = { sort: null };
    }
    return json;
  };
  function clearBasicSearch() {
    setSearchUrl({
      caseId: caseId,
    });
    dispatch(
      setComponentState1({
        entityName: "tasksListTabState",
        entityState: {
          PageNumber: 1,
          SearchText: "",
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          formValues: null,
          clearSearch: false,
        },
      })
    );

    setFormValues((prevValues: any) => ({
      ...prevValues,
      ClaimReference: "",
      Name: "",
      Overdue: "",
      Status: "",
      Type: "",
      DueDateStart: "",
      DueDateEnd: "",
      CreatedOnStart: "",
      CreatedOnEnd: "",
    }));
  }
  if (isTasksLoading) {
    return <div>{t("global.globalNotifications.loadingMessage")}</div>;
  }
  return (
    <div
      className={
        "mar-b-20" +
        (clearSearch ? " ccwp_creditor_header" : "") +
        (showBasicSearch ? " hide_claims_table" : "")
      }
    >
      <Table
        columnDefs={columnTasksDefs}
        paginationObj={paginationObj}
        columnTypes={{ ...retainState.sortedColumnTypes }}
        handlePaginationData={handlePaginationData}
        cellRenderers={
          tableDataFetching || tableDataError ? [] : tableData ? tableData : []
        }
        TitleComplement={() => (
          <TitleComplement
            showBasicSearch={showBasicSearch}
            setShowBasicSearch={setShowBasicSearch}
            clearBasicSearch={clearBasicSearch}
            clearSearch={clearSearch}
          />
        )}
        title={"pages.tasks.taskGridTitle"}
        description={"pages.tasks.taskGridDescription"}
        overlayNoRowsTemplate={
          tableDataFetching
            ? t(tableLoadingData)
            : tableDataError
            ? t("global.globalNotifications.wentWrong")
            : t("global.globalNotifications.noRecords")
        }
        caseRenderers={caseRenderers}
      />
      {!showBasicSearch && (
        <CustomPagination
          apiData={tasksData}
          apiStatus={tasksStatus}
          currentpage={paginationObj.PageNumber}
          pageSize={paginationObj.PageSize}
          disableNext={!tableDataHasRecords}
          onChangePageSize={(page: number) => {
            dispatch(
              setComponentState1({
                entityName: "tasksListTabState",
                entityState: {
                  PageSize: page,
                  PageNumber: 1,
                },
              })
            );
          }}
          onChangeNextPage={() => {
            dispatch(
              setComponentState1({
                entityName: "tasksListTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber + 1,
                },
              })
            );
          }}
          onChangePreviousPage={() => {
            dispatch(
              setComponentState1({
                entityName: "tasksListTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber - 1,
                },
              })
            );
          }}
          pageSizeSelector={paginationPageSizeSelector}
        />
      )}
      {showBasicSearch && (
        <BasicSearch
          handleChange={handleChange}
          setShowBasicSearch={setShowBasicSearch}
          handleSubmit={handleSubmit}
          formValues={formValues}
          setFormValues={setFormValues}
        />
      )}
    </div>
  );
};
export default Tasks;
